/**
 * MobileBottomNav — fixed-position bottom tab bar shown only on small screens.
 *
 * 4 tabs covering the daily workflows: Briefing, Clients, Actions, Chat.
 * Auto-hides on md+ (where the sidebar takes over). Tap-targets are 56px tall.
 * Active tab shows accent color + filled icon. Chat tab opens the ChatPanel
 * instead of navigating (the panel is mounted globally in DashboardLayout).
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useBasePath } from '@/lib/config/nav-helpers';

type TabId = 'briefing' | 'clients' | 'actions' | 'chat';

interface Tab {
  id: TabId;
  label: string;
  path: string;
  matchPrefix: string;
  icon: (active: boolean) => JSX.Element;
}

const TABS: Tab[] = [
  {
    id: 'briefing',
    label: 'Home',
    path: '',
    matchPrefix: '',
    icon: (active) => (
      <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      </svg>
    ),
  },
  {
    id: 'clients',
    label: 'Clients',
    path: '/clients',
    matchPrefix: '/clients',
    icon: (active) => (
      <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    id: 'actions',
    label: 'Actions',
    path: '/actions',
    matchPrefix: '/actions',
    icon: (active) => (
      <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={active ? 0 : 2} strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
  },
  {
    id: 'chat',
    label: 'Ask',
    path: '#chat',
    matchPrefix: '__never__',
    icon: () => (
      <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
      </svg>
    ),
  },
];

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useBasePath();

  function isActive(t: Tab): boolean {
    const rest = location.pathname.replace(basePath, '') || '/';
    if (t.matchPrefix === '') return rest === '/' || rest === '';
    return rest.startsWith(t.matchPrefix);
  }

  function handleClick(t: Tab) {
    if (t.id === 'chat') {
      // The ChatPanel listens for this custom event from anywhere in the app.
      window.dispatchEvent(new CustomEvent('waypoint:open-chat'));
      return;
    }
    navigate(`${basePath}${t.path}`);
  }

  return (
    <nav
      className={clsx(
        'fixed bottom-0 inset-x-0 z-dropdown md:hidden',
        'border-t border-border bg-bg-1/95 backdrop-blur-md',
        'pb-[env(safe-area-inset-bottom)]',     // honor iOS home-indicator
      )}
      role="navigation"
      aria-label="Primary"
    >
      <div className="grid grid-cols-4">
        {TABS.map(t => {
          const active = isActive(t);
          return (
            <button
              key={t.id}
              onClick={() => handleClick(t)}
              className={clsx(
                'flex flex-col items-center justify-center gap-0.5 h-14 transition-colors',
                active ? 'text-primary' : 'text-ink-dim hover:text-ink-muted',
              )}
              aria-current={active ? 'page' : undefined}
            >
              {t.icon(active)}
              <span className="text-[9.5px] font-medium">{t.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
